import type { SurveyProps } from '@shared/components/Survey';
import { FormType } from '@graphql/generated';
import type { SurveyStepProps } from './steps/types';
import { useEventFormState, EventFormValues } from './hooks/useEventFormState';
import React, { useState, useEffect } from 'react';
import { EventFormStep, useEventFormNav, GuestInfo } from './hooks/useEventFormNav';
import { Box, Flex, TextV2, useTheme } from '@withjoy/joykit';
import { EventFormAttendanceStep } from './steps/AttendanceStep/AttendanceStep';
import { EventFormContactStep } from './steps/ContactStep/ContactStep';
import { EventFormSurveyStep } from './steps/SurveyStep/SurveyStep';
import { ConfirmGuestStep } from './steps/ConfirmGuestStep/ConfirmGuestStep';
import { DisabledStep } from './steps/DisabledStep/DisabledStep';
import { StyledControl } from '@withjoy/joykit/components/CheckboxV2/Checkbox.styles';
import { ReviewStep } from './steps/ReviewStep/ReviewStep';
import { useColorPaletteProvider } from '@apps/guest';
import { StyledEventBackButton, StyledEventFormButton } from './styles';
import { useNavButtonStyles } from './hooks/useNavButtonStyles';
import { animationTransition } from '@shared/utils/animationTransition';

export type FieldVisibility = 'optional' | 'required' | 'hidden';

export type EventFormEditorProps = {
  /** provide from useColorPaletteProvider to inject guest site styles */
  colors?: ReturnType<typeof useColorPaletteProvider>;
  settings: {
    isRegistration: boolean;
    hasAttendanceQuestion: boolean;
    phoneFields: FieldVisibility;
    addressFields: FieldVisibility;
  };
  formData: SurveyProps['config'];
  initialData?: Readonly<Partial<EventFormValues>>;
  onSubmit?: (values: EventFormValues) => void;
  guestInfo?: GuestInfo;
  isFormDisabled: boolean;
  formType: FormType;
};

const getEventFormType = (settings: EventFormEditorProps['settings']): 'registrationAttendance' | 'registrationNoAttendance' | 'form' => {
  if (settings.isRegistration) {
    return settings.hasAttendanceQuestion ? 'registrationAttendance' : 'registrationNoAttendance';
  }
  return 'form';
};

export const EventFormEditor = (props: EventFormEditorProps) => {
  const { settings, colors: guestSiteColors, onSubmit, guestInfo, isFormDisabled, formType } = props;
  const { step, setStep, hasNextStep, hasPrevStep, processNavigation, handleNotAttending, handleAttending } = useEventFormNav(
    getEventFormType(settings),
    isFormDisabled,
    guestInfo,
  );
  const { formState, setFormState } = useEventFormState(props.initialData ?? {});

  // todo: revisit guest site colors.
  // What if page color is white? Button would be white.
  // What if page color is dark and text is white? Checkmarks and some text would be very low contrast.
  const { colors } = useTheme();
  const { solidButtonStyles, outlineButtonStyles } = useNavButtonStyles(guestSiteColors);

  const handleSubmit = () => {
    if (formState.attendanceValue === false) {
      // There are cases where the user changes their attendance answer from yes to no after filling out questions.
      // this protects us from submitting questions answers that should only be submitted for a yes attendance
      // Waiting to clear until here allows the user to toggle between yes an no without losing answers they already provided
      onSubmit?.({ ...formState, formValues: {} });
    } else {
      onSubmit?.(formState);
    }
  };

  return (
    <Flex
      flexDirection="column"
      width={['100%', null, `375px`]}
      __css={{
        [StyledControl]: {
          backgroundColor: 'mono2',
          border: 0,
          color: `${guestSiteColors?.pageColor ?? colors.newUIBrandPrimary}!important`,
          _focus: {
            boxShadow: 'none',
          },
        },
        [StyledEventFormButton]: {
          ...solidButtonStyles,
        },
        [StyledEventBackButton]: {
          ...outlineButtonStyles,
        },
      }}
      minHeight="100vh"
      height="fit-content"
    >
      <RenderStep
        step={step}
        props={props}
        formState={formState}
        setFormState={setFormState}
        onNext={() => processNavigation('next')}
        onBack={() => processNavigation('prev')}
        hasNext={hasNextStep}
        hasPrev={hasPrevStep}
        onSkip={() => {
          setStep('done');
        }}
        onSubmitNotAttending={() => {
          // not attending = not answering the survey.
          // it might be populated if we have existing answers or went back.
          // so clear it out.
          handleNotAttending();
        }}
        onAttending={() => {
          handleAttending();
        }}
        onSubmit={handleSubmit}
        guestInfo={guestInfo}
        formType={formType}
      />
    </Flex>
  );
};

type RenderStepProps = {
  step: EventFormStep;
  props: EventFormEditorProps;
  formState: EventFormValues;
  setFormState: React.Dispatch<React.SetStateAction<EventFormValues>>;
  onNext: () => void;
  onBack: () => void;
  hasNext: boolean;
  hasPrev: boolean;
  onSkip: () => void;
  onSubmitNotAttending: () => void;
  onAttending: () => void;
  onSubmit: () => void;
  guestInfo?: GuestInfo;
  formType: FormType;
};

const RenderStep = (props: RenderStepProps) => {
  const { step, props: editorProps, formState, setFormState, onNext, onBack, hasNext, hasPrev, onSkip, onSubmitNotAttending, onAttending, onSubmit, guestInfo, formType } = props;
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentStep, setCurrentStep] = useState(step);
  // buffer navigation changes during transition
  // to avoid visual jank on clicking next/prev
  const [currentNav, setCurrentNav] = useState({ hasNext, hasPrev });

  useEffect(() => {
    if (step !== currentStep) {
      setIsTransitioning(true);
      const timer = setTimeout(() => {
        setCurrentStep(step);
        setCurrentNav({ hasNext, hasPrev });
        setIsTransitioning(false);
      }, 150); // Half of the transition duration for crossfade effect
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [step, currentStep, hasNext, hasPrev]);

  const sharedProps: SurveyStepProps = {
    settings: editorProps.settings,
    formData: editorProps.formData,
    colors: editorProps.colors,
    formState,
    setFormState,
    onNext,
    onBack,
    onSkip,
    onSubmitNotAttending,
    onAttending,
    hasNext: currentNav.hasNext,
    hasPrev: currentNav.hasPrev,
    onSubmit,
    guestInfo,
  };

  const stepContent = (() => {
    switch (currentStep) {
      case 'confirmGuest':
        return <ConfirmGuestStep {...sharedProps} />;
      case 'attendance':
        return <EventFormAttendanceStep {...sharedProps} />;
      case 'contactInfo':
        return <EventFormContactStep {...sharedProps} />;
      case 'survey':
        return <EventFormSurveyStep {...sharedProps} />;
      case 'done':
        return <ReviewStep {...sharedProps} />;
      case 'disabled':
        return <DisabledStep formType={formType} />;
      default:
        return <TextV2>Unknown state</TextV2>;
    }
  })();

  return (
    <Box
      opacity={isTransitioning ? 0 : 1}
      transition={animationTransition('opacity', { duration: '300ms' })}
    >
      {stepContent}
    </Box>
  );
};
